import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  Shield, CheckCircle, Database, GitBranch, Lock, Search, Users,
  ChevronRight, ChevronLeft, AlertCircle, AlertTriangle,
  Loader2, Building2, Mail, User, Briefcase, DollarSign,
  TrendingUp, Zap, Clock, Download, ArrowRight, Home,
  Lightbulb, Award, Rocket, PieChart, Activity, FileText, BarChart3, Target,
  MessageSquare, ChevronDown, ChevronUp, ExternalLink, Globe, FileDown,
  ArrowUpRight, Milestone, Calendar, UserPlus, Coins, TrendingDown, Flag
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ReportSecurityNote } from '../components/ReportSecurityNote';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import axios from 'axios';
import { submitMaturity } from '../utils/maturityJobs';
import { toast } from 'sonner';
import DataMaturityComparison from '../components/data-maturity/DataMaturityComparison';
import { generateDataMaturityPptx } from '../utils/generateDataMaturityPptx';
import AGIInsightsTab from '../components/agi/AGIInsightsTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping for dimensions
const iconMap = {
  Shield: Shield,
  CheckCircle: CheckCircle,
  Database: Database,
  GitBranch: GitBranch,
  Lock: Lock,
  Search: Search,
  Users: Users
};

// Company size options
const companySizes = [
  { value: "1-50", label: "1-50 employees" },
  { value: "51-200", label: "51-200 employees" },
  { value: "201-1000", label: "201-1,000 employees" },
  { value: "1001-5000", label: "1,001-5,000 employees" },
  { value: "5000+", label: "5,000+ employees" }
];

// Revenue options
const revenueRanges = [
  { value: "$0-1M", label: "Under $1 Million" },
  { value: "$1M-10M", label: "$1M - $10 Million" },
  { value: "$10M-100M", label: "$10M - $100 Million" },
  { value: "$100M-1B", label: "$100M - $1 Billion" },
  { value: "$1B+", label: "Over $1 Billion" }
];

// Industries - all 33 supported industries
const industries = [
  'Aerospace & Defense', 'Agriculture', 'Automotive', 'Biotechnology', 'Chemicals',
  'Consumer Packaged Goods', 'Cosmetics & Personal Care', 'Education', 'Energy',
  'EPC (Engineering, Procurement & Construction)',
  'Financial Services', 'Food Processing', 'Healthcare', 'Insurance', 'Manufacturing',
  'Media & Entertainment', 'Medical Devices', 'Metals & Mining', 'Nonprofit', 'Oil & Gas',
  'Pharmaceuticals', 'Private Equity', 'Professional Services', 'Pulp & Paper',
  'Real Estate & Construction', 'Restaurant', 'Retail', 'Social Sector & NGOs', 'Software',
  'Technology', 'Telecommunications', 'Travel & Logistics', 'Utilities', 'Waste Management', 'Wine, Beer & Spirits'
];

const DataMaturityPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const assessmentIdFromUrl = searchParams.get('id');
  
  const [step, setStep] = useState(0); // 0: intro, 1: profile, 2+: dimensions, final: results
  const [loading, setLoading] = useState(false);
  const [loadingAssessment, setLoadingAssessment] = useState(false);
  const [questionsData, setQuestionsData] = useState(null);
  const [responses, setResponses] = useState({});
  const [results, setResults] = useState(null);
  const [fullResults, setFullResults] = useState(null);
  const [error, setError] = useState('');
  const [secretCode, setSecretCode] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [showContextInput, setShowContextInput] = useState(false);
  const [assessmentId, setAssessmentId] = useState(assessmentIdFromUrl || null);
  
  // Profile form state
  const [profile, setProfile] = useState({
    company_name: '',
    company_url: '',
    industry: '',
    company_size: '',
    annual_revenue: '',
    respondent_name: '',
    respondent_role: '',
    respondent_email: ''
  });
  
  // URL scraping state
  const [scrapingUrl, setScrapingUrl] = useState(false);
  const [scrapedData, setScrapedData] = useState(null);

  // Fetch questions on mount
  useEffect(() => {
    fetchQuestions();
  }, []);
  
  // Load assessment from URL if id parameter is present
  useEffect(() => {
    if (assessmentIdFromUrl) {
      loadAssessmentFromUrl(assessmentIdFromUrl);
    }
  }, [assessmentIdFromUrl]);
  
  const loadAssessmentFromUrl = async (id) => {
    setLoadingAssessment(true);
    try {
      const res = await axios.get(`${API}/data-maturity/assessment/${id}`);
      if (res.data) {
        // Set assessment ID
        setAssessmentId(id);
        
        // Build sample results from the stored assessment
        const assessment = res.data;
        // Check both code_verified/secret_code_used and is_sample_preview flags
        const isSamplePreview = assessment.is_sample_preview === true || assessment.sample_only === true;
        const isVerified = (assessment.code_verified === true || assessment.secret_code_used === true) && !isSamplePreview;
        
        // Store email in profile for verification
        if (assessment.respondent_email) {
          setProfile(prev => ({ ...prev, respondent_email: assessment.respondent_email }));
        }
        
        setResults({
          success: true,
          assessment_id: id,
          id: id,
          respondent_email: assessment.respondent_email,
          requires_secret_code: !isVerified,
          is_sample_preview: isSamplePreview,
          sample_results: {
            company_name: assessment.company_name,
            industry: assessment.industry,
            overall_score: assessment.overall_score,
            overall_maturity_level: assessment.overall_maturity_level || assessment.maturity_level?.level,
            overall_maturity_description: assessment.maturity_level?.description || '',
            maturity_color: assessment.maturity_color || assessment.maturity_level?.color || '#6366F1',
            industry_benchmark: assessment.industry_benchmark,
            top_performer_score: assessment.top_performer_score || 4.0,
            dimension_scores: assessment.dimension_scores || [],
            total_dimensions_count: assessment.total_dimensions_count || assessment.dimension_scores?.length || 6,
            critical_pain_points: (assessment.critical_pain_points || []).slice(0, 2),
            executive_summary_preview: assessment.executive_summary_preview || ''
          }
        });
        
        // Only set full results if verified and not a sample preview
        if (isVerified && !isSamplePreview) {
          setFullResults(assessment);
        }
        
        // Jump to results step - use a high number to trigger results view
        setStep(999);
      }
    } catch (err) {
      console.error('Error loading assessment:', err);
      setError('Assessment not found or has expired. Please start a new assessment.');
      setLoadingAssessment(false);
    } finally {
      setLoadingAssessment(false);
    }
  };

  const fetchQuestions = async () => {
    try {
      const res = await axios.get(`${API}/data-maturity/questions`);
      setQuestionsData(res.data);
    } catch (err) {
      console.error('Error fetching questions:', err);
      setError('Failed to load assessment questions');
    }
  };

  const dimensions = questionsData ? Object.keys(questionsData.dimensions) : [];
  const totalSteps = dimensions.length + 2;

  const handleProfileChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleResponseChange = (questionId, value) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
  };

  const validateProfile = () => {
    const required = ['company_name', 'industry', 'company_size', 'annual_revenue', 'respondent_name', 'respondent_role', 'respondent_email'];
    for (const field of required) {
      if (!profile[field]) {
        setError(`Please fill in all required fields`);
        return false;
      }
    }
    if (!profile.respondent_email.includes('@')) {
      setError('Please enter a valid email address');
      return false;
    }
    return true;
  };

  // Scrape company URL for data management reports
  const scrapeCompanyUrl = async () => {
    if (!profile.company_url) return;
    
    setScrapingUrl(true);
    try {
      const formData = new FormData();
      formData.append('url', profile.company_url);
      formData.append('report_type', 'data_management');
      
      const res = await axios.post(`${API}/scrape-company-report`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });
      
      setScrapedData(res.data);
    } catch (err) {
      console.error('Error scraping URL:', err);
      setScrapedData({ success: false, error: 'Failed to analyze URL' });
    } finally {
      setScrapingUrl(false);
    }
  };

  const validateDimension = (dimIndex) => {
    const dimId = dimensions[dimIndex];
    const questions = questionsData.dimensions[dimId].questions;
    for (const q of questions) {
      if (!responses[q.id]) {
        setError('Please answer all questions before proceeding');
        return false;
      }
    }
    return true;
  };

  const nextStep = () => {
    setError('');
    if (step === 1 && !validateProfile()) return;
    if (step > 1 && step < totalSteps - 1) {
      if (!validateDimension(step - 2)) return;
    }
    setStep(prev => prev + 1);
  };

  const prevStep = () => {
    setError('');
    setStep(prev => prev - 1);
  };

  const downloadPdf = async () => {
    try {
      const id = fullResults?.id || assessmentId;
      const response = await axios.get(`${API}/data-maturity/assessment/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(fullResults?.company_name || 'Company').replace(/[^a-zA-Z0-9_-]/g, '_')}_Data_Maturity.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('PDF download could not complete. Your saved assessment is still available.');
    }
  };

  const submitAssessment = async () => {
    setLoading(true);
    setError('');
    
    try {
      const formData = new FormData();
      formData.append('company_name', profile.company_name);
      formData.append('industry', profile.industry);
      formData.append('company_size', profile.company_size);
      formData.append('annual_revenue', profile.annual_revenue);
      formData.append('respondent_name', profile.respondent_name);
      formData.append('respondent_role', profile.respondent_role);
      formData.append('respondent_email', profile.respondent_email);
      formData.append('responses', JSON.stringify(responses));
      if (additionalContext.trim()) {
        formData.append('additional_context', additionalContext.trim());
      }
      if (profile.company_url) {
        formData.append('company_url', profile.company_url);
      }
      if (scrapedData?.success && scrapedData?.extracted_info) {
        formData.append('scraped_data', JSON.stringify(scrapedData.extracted_info));
      }

      const res = await submitMaturity(`${API}/data-maturity/assess`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000
      });

      setResults(res.data);
      setAssessmentId(res.data.assessment_id);
      setStep(totalSteps);
    } catch (err) {
      console.error('Assessment error:', err);
      setError(err.response?.data?.detail || 'Failed to submit assessment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifySecretCode = async () => {
    if (!secretCode || secretCode.length !== 6) {
      setCodeError('Please enter a valid 6-digit code');
      return;
    }
    
    setVerifyingCode(true);
    setCodeError('');
    
    try {
      const formData = new FormData();
      formData.append('assessment_id', assessmentId || results.assessment_id || results.id);
      formData.append('secret_code', secretCode);
      // Use profile email, fall back to results email
      const email = profile.respondent_email || results.respondent_email || '';
      formData.append('email', email);
      
      const res = await axios.post(`${API}/data-maturity/verify-code`, formData);
      
      if (res.data.verified) {
        setFullResults(res.data.full_results);
      }
    } catch (err) {
      // Handle different error response formats
      const detail = err.response?.data?.detail;
      let errorMessage = 'Invalid secret code';
      
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(e => e.msg || e.message).join(', ') || errorMessage;
      } else if (detail && typeof detail === 'object') {
        errorMessage = detail.msg || detail.message || errorMessage;
      }
      
      setCodeError(errorMessage);
    } finally {
      setVerifyingCode(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 4.2) return 'text-green-600';
    if (score >= 3.4) return 'text-emerald-500';
    if (score >= 2.6) return 'text-yellow-500';
    if (score >= 1.8) return 'text-orange-500';
    return 'text-red-500';
  };

  const getScoreBgColor = (score) => {
    if (score >= 4.2) return 'bg-green-100';
    if (score >= 3.4) return 'bg-emerald-100';
    if (score >= 2.6) return 'bg-yellow-100';
    if (score >= 1.8) return 'bg-orange-100';
    return 'bg-red-100';
  };

  // Loading state when loading assessment from URL
  if (loadingAssessment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Loading Your Assessment...</h2>
          <p className="text-gray-500 mt-2">Please wait while we retrieve your results</p>
        </div>
      </div>
    );
  }

  // Intro Step
  if (step === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-white">
        <header className="bg-white/80 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Database className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">SAMANVAI</span>
            </Link>
            <Link to="/">
              <Button variant="outline" className="gap-2">
                <Home className="w-4 h-4" />
                Home
              </Button>
            </Link>
          </div>
        </header>

        <div className="max-w-5xl mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Database className="w-4 h-4" />
              Data Management Maturity Assessment
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Assess Your Organization's
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600"> Data Maturity</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive assessment based on DAMA-DMBOK, CMMI DMM, and industry best practices.
              Get actionable insights to transform your data management capabilities.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              { icon: Shield, title: 'Data Governance', desc: 'Ownership, policies, compliance' },
              { icon: CheckCircle, title: 'Data Quality', desc: 'Accuracy, completeness, consistency' },
              { icon: Database, title: 'Architecture', desc: 'Storage, scalability, DR' },
              { icon: GitBranch, title: 'Integration', desc: 'ETL, APIs, lineage' },
              { icon: Lock, title: 'Security', desc: 'Encryption, privacy, risk' },
              { icon: Search, title: 'Metadata', desc: 'Discovery, cataloging' },
              { icon: Users, title: 'Data Culture', desc: 'Literacy, self-service' },
              { icon: BarChart3, title: 'Benchmarks', desc: 'Industry comparisons' }
            ].map((item, idx) => (
              <div key={idx} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mb-3">
                  <item.icon className="w-5 h-5 text-indigo-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* What You Get */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">What You'll Get</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { icon: BarChart3, title: 'Industry Benchmark Comparison', desc: 'See how you stack up against industry peers and top performers' },
                { icon: AlertTriangle, title: 'Critical Pain Points', desc: 'Identify areas requiring immediate attention with severity ratings' },
                { icon: Rocket, title: '12-Month Transformation Roadmap', desc: 'Phased implementation plan with milestones and success metrics' },
                { icon: Lightbulb, title: 'Technology Recommendations', desc: 'Data product suggestions with cost-benefit analysis' },
                { icon: FileText, title: 'Compliance Assessment', desc: 'GDPR, DPDP Act readiness and risk evaluation' },
                { icon: Download, title: 'Executive Presentation', desc: 'Downloadable PowerPoint for stakeholder communication' }
              ].map((item, idx) => (
                <div key={idx} className="flex gap-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{item.title}</h3>
                    <p className="text-sm text-gray-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <Button 
              onClick={() => setStep(1)} 
              size="lg"
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-12 py-6 text-lg gap-3"
              data-testid="start-assessment-btn"
            >
              Start Assessment
              <ArrowRight className="w-5 h-5" />
            </Button>
            <p className="text-sm text-gray-500 mt-4">Takes approximately 10-15 minutes to complete</p>
          </div>
        </div>
      </div>
    );
  }

  // Profile Step (Step 1)
  if (step === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-white py-8">
        <div className="max-w-2xl mx-auto px-4">
          <div className="mb-8">
            <Progress value={(1 / totalSteps) * 100} className="h-2" />
            <p className="text-sm text-gray-500 mt-2 text-center">Step 1 of {totalSteps}</p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-indigo-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Organization Profile</h2>
              <p className="text-gray-500">Tell us about your organization</p>
            </div>

            <div className="space-y-6">
              {/* Company Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company Name *</label>
                <Input
                  value={profile.company_name}
                  onChange={(e) => handleProfileChange('company_name', e.target.value)}
                  placeholder="Enter company name"
                  data-testid="input-company"
                />
              </div>

              {/* Company URL for scraping */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Website / Data Strategy Report URL
                  <span className="text-xs text-gray-500 ml-1">(Optional - we'll analyze it)</span>
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      value={profile.company_url}
                      onChange={(e) => handleProfileChange('company_url', e.target.value)}
                      placeholder="https://company.com/data-strategy"
                      className="pr-10"
                      data-testid="input-company-url"
                    />
                    <Globe className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  </div>
                  {profile.company_url && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={scrapeCompanyUrl}
                      disabled={scrapingUrl}
                      className="px-3"
                    >
                      {scrapingUrl ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                    </Button>
                  )}
                </div>
                {scrapedData?.success && (
                  <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Report analyzed - insights will inform your assessment
                  </p>
                )}
                {scrapedData?.success === false && (
                  <p className="text-xs text-gray-500 mt-1">Could not analyze URL. Assessment will proceed normally.</p>
                )}
              </div>

              {/* Industry */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Industry *</label>
                <Select value={profile.industry} onValueChange={(v) => handleProfileChange('industry', v)}>
                  <SelectTrigger data-testid="select-industry">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {industries.map((ind) => (
                      <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Company Size & Revenue */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Company Size *</label>
                  <Select value={profile.company_size} onValueChange={(v) => handleProfileChange('company_size', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      {companySizes.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Annual Revenue *</label>
                  <Select value={profile.annual_revenue} onValueChange={(v) => handleProfileChange('annual_revenue', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select revenue" />
                    </SelectTrigger>
                    <SelectContent>
                      {revenueRanges.map((r) => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Respondent Info */}
              <div className="pt-4 border-t">
                <h3 className="font-medium text-gray-900 mb-4">Your Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Your Name *</label>
                    <Input
                      value={profile.respondent_name}
                      onChange={(e) => handleProfileChange('respondent_name', e.target.value)}
                      placeholder="Full name"
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Your Role *</label>
                    <Input
                      value={profile.respondent_role}
                      onChange={(e) => handleProfileChange('respondent_role', e.target.value)}
                      placeholder="Job title"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <Input
                    type="email"
                    value={profile.respondent_email}
                    onChange={(e) => handleProfileChange('respondent_email', e.target.value)}
                    placeholder="work@company.com"
                    data-testid="input-email"
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-600 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setStep(0)} className="gap-2">
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </Button>
                <Button onClick={nextStep} className="bg-indigo-600 hover:bg-indigo-700 gap-2">
                  Continue
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Question Steps
  if (step > 1 && step < totalSteps && questionsData) {
    const dimIndex = step - 2;
    const dimId = dimensions[dimIndex];
    const dimension = questionsData.dimensions[dimId];
    const IconComponent = iconMap[dimension.icon] || Database;

    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-white py-8">
        <div className="max-w-3xl mx-auto px-4">
          <div className="mb-8">
            <Progress value={(step / totalSteps) * 100} className="h-2" />
            <p className="text-sm text-gray-500 mt-2 text-center">Step {step} of {totalSteps}</p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center gap-4 mb-6 pb-6 border-b">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${dimension.color}20` }}>
                <IconComponent className="w-7 h-7" style={{ color: dimension.color }} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{dimension.name}</h2>
                <p className="text-gray-500 text-sm">{dimension.description}</p>
              </div>
            </div>

            <div className="space-y-8">
              {dimension.questions.map((q, qIdx) => (
                <div key={q.id} className="space-y-4">
                  <h3 className="font-medium text-gray-900">
                    {qIdx + 1}. {q.question}
                  </h3>
                  <div className="space-y-2">
                    {q.options.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => handleResponseChange(q.id, opt.value)}
                        className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                          responses[q.id] === opt.value
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-gray-200 hover:border-indigo-300'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-6 h-6 rounded-full border-2 flex-shrink-0 flex items-center justify-center mt-0.5 ${
                            responses[q.id] === opt.value ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300'
                          }`}>
                            {responses[q.id] === opt.value && (
                              <CheckCircle className="w-4 h-4 text-white" />
                            )}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">
                              Level {opt.value}: {opt.label}
                            </div>
                            <div className="text-sm text-gray-500 mt-1">{opt.description}</div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {error && (
              <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-3 text-red-600 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            {/* Additional Context Input - shown on last dimension */}
            {dimIndex === dimensions.length - 1 && (
              <div className="mt-8">
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setShowContextInput(!showContextInput)}
                    className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-white/50 transition-colors"
                    data-testid="toggle-context-input"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center">
                        <MessageSquare className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Additional Context (Optional)</h4>
                        <p className="text-sm text-gray-600">Share specific challenges, goals, or context for personalized insights</p>
                      </div>
                    </div>
                    {showContextInput ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </button>
                  {showContextInput && (
                    <div className="px-6 pb-6">
                      <textarea
                        value={additionalContext}
                        onChange={(e) => setAdditionalContext(e.target.value)}
                        placeholder="Examples:&#10;• We struggle with data silos across departments&#10;• Our data quality is a major concern for reporting&#10;• We want to build a modern data platform&#10;• Regulatory compliance (GDPR, HIPAA) is a priority"
                        className="w-full h-32 p-4 border border-gray-200 rounded-lg resize-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-700 placeholder:text-gray-400"
                        maxLength={1000}
                        data-testid="additional-context-input"
                      />
                      <div className="flex justify-between items-center mt-2">
                        <p className="text-xs text-gray-500">
                          This helps our AI provide more relevant recommendations
                        </p>
                        <span className="text-xs text-gray-400">{additionalContext.length}/1000</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-between mt-8 pt-6 border-t">
              <Button variant="outline" onClick={prevStep} className="gap-2">
                <ChevronLeft className="w-4 h-4" />
                Back
              </Button>
              {dimIndex < dimensions.length - 1 ? (
                <Button onClick={nextStep} className="bg-indigo-600 hover:bg-indigo-700 gap-2">
                  Continue
                  <ChevronRight className="w-4 h-4" />
                </Button>
              ) : (
                <Button 
                  onClick={submitAssessment} 
                  disabled={loading}
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      Complete Assessment
                      <CheckCircle className="w-4 h-4" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Results Step
  if ((step === totalSteps || step >= 999) && results) {
    const sample = results.sample_results;
    
    // If full results unlocked
    if (fullResults) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-white py-8">
          <div className="max-w-6xl mx-auto px-4">
            {/* Header */}
            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-8 mb-8">
              <div className="flex flex-col sm:flex-row justify-between items-start gap-5 mb-6">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                      <CheckCircle className="w-4 h-4" />
                      Full Report Unlocked
                    </div>
                    <Button
                      onClick={() => generateDataMaturityPptx(results, fullResults)}
                      className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-full text-sm font-medium"
                      data-testid="data-download-pptx"
                    >
                      <FileDown className="w-4 h-4" />
                      Download Presentation
                    </Button>
                    <Button data-testid="data-download-pdf" variant="outline" onClick={downloadPdf} className="gap-2 rounded-full">
                      <FileDown className="w-4 h-4" /> Download PDF
                    </Button>
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900">{fullResults.company_name}</h1>
                  <p className="text-gray-500">{fullResults.industry} • Data Maturity Assessment</p>
                </div>
                <div className="text-left sm:text-right">
                  <div className={`text-5xl font-bold ${getScoreColor(fullResults.overall_score)}`}>
                    {fullResults.overall_score}/5.0
                  </div>
                  <div className="text-lg font-medium" style={{ color: fullResults.maturity_color }}>
                    {fullResults.overall_maturity_level}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    Industry Benchmark: {fullResults.industry_benchmark}/5.0
                  </div>
                </div>
              </div>

              {/* Executive Summary */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="font-bold text-gray-900 mb-3">Executive Summary</h3>
                <p className="text-gray-600 leading-relaxed">
                  {fullResults.ai_analysis?.executive_summary || 'Analysis complete.'}
                </p>
              </div>
            </div>

            {/* Dimension Scores */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Dimension Analysis</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {fullResults.dimension_scores?.map((dim) => {
                  const isUnderperforming = dim.gap_from_benchmark > 0.3;
                  return (
                    <div key={dim.dimension_id} className={`p-4 rounded-xl border-2 ${isUnderperforming ? 'border-red-200 bg-red-50' : 'border-gray-100 bg-gray-50'}`}>
                      <div className="flex justify-between items-start mb-2">
                        <span className={`font-semibold ${isUnderperforming ? 'text-red-800' : 'text-gray-900'}`}>
                          {dim.dimension_name}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${isUnderperforming ? 'bg-red-600 text-white' : 'bg-green-100 text-green-700'}`}>
                          {isUnderperforming ? 'Below Benchmark' : dim.maturity_level}
                        </span>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className={`text-2xl font-bold ${getScoreColor(dim.score)}`}>
                          {dim.score}/5.0
                        </div>
                        {isUnderperforming && (
                          <div className="text-sm text-red-600">
                            Gap: -{dim.gap_from_benchmark}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Industry Benchmark Comparison - NEW */}
            <DataMaturityComparison
              overallScore={fullResults.overall_score}
              industryBenchmark={fullResults.industry_benchmark}
              topPerformerScore={fullResults.top_performer_score}
              dimensionScores={fullResults.dimension_scores}
              industry={fullResults.industry}
              companyName={fullResults.company_name}
            />

            {/* Critical Pain Points */}
            {fullResults.ai_analysis?.critical_pain_points?.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
                <h2 className="text-xl font-bold text-red-700 mb-6 flex items-center gap-2">
                  <AlertTriangle className="w-6 h-6" />
                  Critical Pain Points
                </h2>
                <div className="space-y-4">
                  {fullResults.ai_analysis.critical_pain_points.map((pp, idx) => (
                    <div key={idx} className="p-4 bg-red-50 rounded-xl border border-red-200">
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-bold text-red-800">{pp.area}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                          pp.severity === 'High' ? 'bg-red-600 text-white' : 'bg-orange-500 text-white'
                        }`}>
                          {pp.severity}
                        </span>
                      </div>
                      <p className="text-red-700 text-sm mb-2">{pp.current_impact}</p>
                      <p className="text-gray-700 text-sm"><strong>Recommended:</strong> {pp.recommended_action}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* KPI Improvement Targets - NEW SECTION */}
            {fullResults.ai_analysis?.kpi_improvement_targets?.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                  KPI Improvement Targets
                </h2>
                <p className="text-gray-500 text-sm mb-6">Industry-specific metrics with measurable targets</p>
                
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="kpi-targets-table">
                    <thead>
                      <tr className="bg-gradient-to-r from-indigo-50 to-purple-50">
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 rounded-tl-lg">KPI Name</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Category</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Current</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Benchmark</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">6-Month Target</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">12-Month Target</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 rounded-tr-lg">Priority</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fullResults.ai_analysis.kpi_improvement_targets.map((kpi, idx) => (
                        <tr key={idx} className={`border-b border-gray-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900 text-sm">{kpi.kpi_name}</div>
                            {kpi.business_impact && (
                              <div className="text-xs text-gray-500 mt-1">{kpi.business_impact}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              kpi.category === 'Industry-Specific' 
                                ? 'bg-purple-100 text-purple-700' 
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {kpi.category || 'Common'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-red-600 font-medium text-sm">{kpi.current_estimate}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-gray-600 text-sm">{kpi.benchmark_avg}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-yellow-600 font-medium text-sm">{kpi.target_6_months}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-emerald-600 font-bold text-sm">{kpi.target_12_months}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                              kpi.priority === 'High' ? 'bg-red-100 text-red-700' : 
                              kpi.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' : 
                              'bg-green-100 text-green-700'
                            }`}>
                              {kpi.priority}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Industry KPIs Badge */}
                {fullResults.industry_kpis?.length > 0 && (
                  <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Activity className="w-5 h-5 text-purple-600" />
                      <span className="font-semibold text-purple-800">
                        {fullResults.industry_kpis.length} Industry-Specific KPIs for {fullResults.industry}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {fullResults.industry_kpis.slice(0, 6).map((kpi, idx) => (
                        <span key={idx} className="px-3 py-1 bg-white rounded-full text-xs text-purple-700 border border-purple-200">
                          {kpi.name}
                        </span>
                      ))}
                      {fullResults.industry_kpis.length > 6 && (
                        <span className="px-3 py-1 bg-purple-600 text-white rounded-full text-xs">
                          +{fullResults.industry_kpis.length - 6} more
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Roadmap */}
            {fullResults.ai_analysis?.roadmap && (
              <div className="bg-white rounded-2xl shadow-lg p-8 mb-8" data-testid="transformation-roadmap">
                <h2 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <Rocket className="w-6 h-6 text-indigo-600" />
                  12-Month Transformation Roadmap
                </h2>
                <p className="text-gray-500 text-sm mb-6">Detailed strategy, investment, benefits, ROI analysis, and risk mitigation</p>
                
                {/* Total Program Investment Summary */}
                {fullResults.ai_analysis.roadmap.total_program_investment && (
                  <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 mb-8 text-white">
                    <h3 className="font-bold text-lg mb-4">Total Program Investment Summary</h3>
                    <div className="grid md:grid-cols-4 gap-4">
                      <div className="bg-white/10 rounded-lg p-4">
                        <div className="text-indigo-200 text-xs uppercase mb-1">Year 1 Investment</div>
                        <div className="text-2xl font-bold">{fullResults.ai_analysis.roadmap.total_program_investment.year_1_total || 'TBD'}</div>
                      </div>
                      <div className="bg-white/10 rounded-lg p-4">
                        <div className="text-indigo-200 text-xs uppercase mb-1">Expected Benefits</div>
                        <div className="text-2xl font-bold">{fullResults.ai_analysis.roadmap.total_program_investment.total_expected_benefits || 'TBD'}</div>
                      </div>
                      <div className="bg-white/10 rounded-lg p-4">
                        <div className="text-indigo-200 text-xs uppercase mb-1">Overall ROI</div>
                        <div className="text-2xl font-bold">{fullResults.ai_analysis.roadmap.total_program_investment.overall_roi || 'TBD'}</div>
                      </div>
                      <div className="bg-white/10 rounded-lg p-4">
                        <div className="text-indigo-200 text-xs uppercase mb-1">Break-Even</div>
                        <div className="text-2xl font-bold">{fullResults.ai_analysis.roadmap.total_program_investment.break_even_point || 'TBD'}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Phase Cards */}
                <div className="space-y-8">
                  {['phase_1', 'phase_2', 'phase_3'].map((phaseKey, idx) => {
                    const phase = fullResults.ai_analysis.roadmap[phaseKey];
                    if (!phase) return null;
                    const phaseColors = [
                      { bg: 'from-emerald-50 to-teal-50', border: 'border-emerald-200', accent: 'text-emerald-700', badge: 'bg-emerald-600' },
                      { bg: 'from-blue-50 to-indigo-50', border: 'border-blue-200', accent: 'text-blue-700', badge: 'bg-blue-600' },
                      { bg: 'from-purple-50 to-pink-50', border: 'border-purple-200', accent: 'text-purple-700', badge: 'bg-purple-600' }
                    ][idx];
                    
                    return (
                      <div key={phaseKey} className={`bg-gradient-to-br ${phaseColors.bg} rounded-xl border-2 ${phaseColors.border} overflow-hidden`}>
                        {/* Phase Header */}
                        <div className={`${phaseColors.badge} text-white p-4`}>
                          <div className="flex justify-between items-center">
                            <div>
                              <span className="text-white/70 text-sm">Phase {idx + 1}</span>
                              <h3 className="text-xl font-bold">{phase.name}</h3>
                            </div>
                            <div className="text-right">
                              <div className="text-white/70 text-sm">Duration</div>
                              <div className="font-bold">{phase.duration}</div>
                            </div>
                          </div>
                        </div>

                        <div className="p-6 space-y-6">
                          {/* Strategy Overview */}
                          {phase.strategy_overview && (
                            <div>
                              <h4 className={`font-bold ${phaseColors.accent} mb-2 flex items-center gap-2`}>
                                <Target className="w-4 h-4" /> Strategy Overview
                              </h4>
                              <p className="text-gray-700 text-sm">{phase.strategy_overview}</p>
                            </div>
                          )}

                          {/* Areas of Focus */}
                          <div>
                            <h4 className={`font-bold ${phaseColors.accent} mb-3 flex items-center gap-2`}>
                              <Lightbulb className="w-4 h-4" /> Areas of Focus
                            </h4>
                            {Array.isArray(phase.focus_areas) && phase.focus_areas.length > 0 && typeof phase.focus_areas[0] === 'object' ? (
                              <div className="grid md:grid-cols-2 gap-3">
                                {phase.focus_areas.map((fa, i) => (
                                  <div key={i} className="bg-white rounded-lg p-4 border border-gray-200">
                                    <div className="flex justify-between items-start mb-2">
                                      <span className="font-medium text-gray-900">{fa.area}</span>
                                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                        fa.priority === 'High' ? 'bg-red-100 text-red-700' : 
                                        fa.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' : 
                                        'bg-green-100 text-green-700'
                                      }`}>{fa.priority}</span>
                                    </div>
                                    <ul className="space-y-1">
                                      {fa.initiatives?.slice(0, 3).map((init, j) => (
                                        <li key={j} className="text-xs text-gray-600 flex items-start gap-1">
                                          <ChevronRight className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                          {init}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <ul className="grid md:grid-cols-2 gap-2">
                                {(phase.focus_areas || phase.key_activities || []).slice(0, 6).map((fa, i) => (
                                  <li key={i} className="text-sm text-gray-700 flex items-start gap-2 bg-white rounded-lg p-3">
                                    <ChevronRight className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                                    {typeof fa === 'string' ? fa : fa.area || fa}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>

                          {/* Investment Required */}
                          {phase.investment_required && (
                            <div>
                              <h4 className={`font-bold ${phaseColors.accent} mb-3 flex items-center gap-2`}>
                                <DollarSign className="w-4 h-4" /> Investment Required
                              </h4>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div className="bg-white rounded-lg p-3 border border-gray-200 text-center">
                                  <div className="text-xs text-gray-500 mb-1">Total Budget</div>
                                  <div className="font-bold text-gray-900 text-sm">{phase.investment_required.total_budget || 'TBD'}</div>
                                </div>
                                <div className="bg-white rounded-lg p-3 border border-gray-200 text-center">
                                  <div className="text-xs text-gray-500 mb-1">Personnel</div>
                                  <div className="font-bold text-gray-900 text-sm">{phase.investment_required.personnel || 'TBD'}</div>
                                </div>
                                <div className="bg-white rounded-lg p-3 border border-gray-200 text-center">
                                  <div className="text-xs text-gray-500 mb-1">Technology</div>
                                  <div className="font-bold text-gray-900 text-sm">{phase.investment_required.technology || 'TBD'}</div>
                                </div>
                                <div className="bg-white rounded-lg p-3 border border-gray-200 text-center">
                                  <div className="text-xs text-gray-500 mb-1">Training</div>
                                  <div className="font-bold text-gray-900 text-sm">{phase.investment_required.training || 'TBD'}</div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Benefits to Realize */}
                          {phase.benefits_to_realize && phase.benefits_to_realize.length > 0 && (
                            <div>
                              <h4 className={`font-bold ${phaseColors.accent} mb-3 flex items-center gap-2`}>
                                <TrendingUp className="w-4 h-4" /> Benefits to Realize
                              </h4>
                              <div className="space-y-2">
                                {phase.benefits_to_realize.slice(0, 4).map((benefit, i) => (
                                  <div key={i} className="bg-white rounded-lg p-3 border border-green-200 flex items-start gap-3">
                                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                                    <div>
                                      <div className="font-medium text-gray-900 text-sm">{benefit.benefit}</div>
                                      <div className="text-xs text-green-700">{benefit.quantified_impact}</div>
                                      <div className="text-xs text-gray-500">{benefit.timeline}</div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* ROI Analysis */}
                          {phase.roi_analysis && (
                            <div>
                              <h4 className={`font-bold ${phaseColors.accent} mb-3 flex items-center gap-2`}>
                                <PieChart className="w-4 h-4" /> ROI & Cost-Benefit Analysis
                              </h4>
                              <div className="bg-white rounded-lg p-4 border border-gray-200">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                  <div>
                                    <div className="text-xs text-gray-500">Investment</div>
                                    <div className="font-bold text-gray-900">{phase.roi_analysis.investment || 'TBD'}</div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-gray-500">Expected Return</div>
                                    <div className="font-bold text-emerald-600">{phase.roi_analysis.expected_return || 'TBD'}</div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-gray-500">Payback Period</div>
                                    <div className="font-bold text-gray-900">{phase.roi_analysis.payback_period || 'TBD'}</div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-gray-500">Cost-Benefit Ratio</div>
                                    <div className="font-bold text-indigo-600">{phase.roi_analysis.cost_benefit_ratio || 'TBD'}</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Risks & Mitigation */}
                          {phase.risks_and_mitigation && phase.risks_and_mitigation.length > 0 && (
                            <div>
                              <h4 className={`font-bold ${phaseColors.accent} mb-3 flex items-center gap-2`}>
                                <AlertTriangle className="w-4 h-4" /> Risks & Mitigation
                              </h4>
                              <div className="space-y-2">
                                {phase.risks_and_mitigation.slice(0, 3).map((risk, i) => (
                                  <div key={i} className="bg-white rounded-lg p-3 border border-amber-200">
                                    <div className="flex justify-between items-start mb-2">
                                      <span className="font-medium text-gray-900 text-sm">{risk.risk}</span>
                                      <div className="flex gap-1">
                                        <span className={`px-2 py-0.5 rounded text-xs ${
                                          risk.likelihood === 'High' ? 'bg-red-100 text-red-700' : 
                                          risk.likelihood === 'Medium' ? 'bg-yellow-100 text-yellow-700' : 
                                          'bg-green-100 text-green-700'
                                        }`}>L: {risk.likelihood}</span>
                                        <span className={`px-2 py-0.5 rounded text-xs ${
                                          risk.impact === 'High' ? 'bg-red-100 text-red-700' : 
                                          risk.impact === 'Medium' ? 'bg-yellow-100 text-yellow-700' : 
                                          'bg-green-100 text-green-700'
                                        }`}>I: {risk.impact}</span>
                                      </div>
                                    </div>
                                    <div className="text-xs text-gray-600">
                                      <strong>Mitigation:</strong> {risk.mitigation_strategy}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Success Metrics */}
                          {phase.success_metrics && phase.success_metrics.length > 0 && (
                            <div>
                              <h4 className={`font-bold ${phaseColors.accent} mb-2 flex items-center gap-2`}>
                                <Award className="w-4 h-4" /> Success Metrics
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {phase.success_metrics.slice(0, 5).map((metric, i) => (
                                  <span key={i} className="px-3 py-1 bg-white rounded-full text-xs text-gray-700 border border-gray-200">
                                    {metric}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Product Recommendations */}
            {fullResults.product_recommendations?.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Technology Recommendations</h2>
                <div className="space-y-6">
                  {fullResults.product_recommendations.map((rec, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-xl p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="font-bold text-gray-900">{rec.category}</h3>
                          <p className="text-sm text-gray-500">For: {rec.dimension}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          rec.priority === 'High' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {rec.priority} Priority
                        </span>
                      </div>
                      <div className="grid md:grid-cols-3 gap-4">
                        {rec.products?.map((prod, pIdx) => (
                          <div key={pIdx} className="bg-gray-50 rounded-lg p-4">
                            <div className="font-medium text-gray-900">{prod.name}</div>
                            <div className="text-xs text-indigo-600 mb-2">{prod.type}</div>
                            <div className="text-sm text-gray-500">{prod.cost_range}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Case Studies */}
            {fullResults.case_studies?.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Industry Case Studies</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  {fullResults.case_studies.map((cs, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-xl p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <Award className="w-5 h-5 text-indigo-600" />
                        <span className="font-bold text-gray-900">{cs.company}</span>
                        <span className="text-sm text-gray-500">({cs.industry})</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-4">{cs.challenge}</p>
                      <div className="bg-green-50 rounded-lg p-3 mb-3">
                        <div className="text-xs text-green-700 font-medium mb-2">Key Results:</div>
                        <ul className="space-y-1">
                          {cs.results?.slice(0, 3).map((r, i) => (
                            <li key={i} className="text-sm text-green-800 flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                              {r}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="text-sm text-indigo-600 font-medium">
                        Maturity: {cs.maturity_improvement}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Level Up Section - Journey to Next Maturity Level */}
            {fullResults.ai_analysis?.level_progression && (
              <div className="bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 rounded-2xl shadow-lg p-8 mb-8 border-2 border-emerald-200" data-testid="level-up-section">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                    <ArrowUpRight className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Level Up: Journey to Next Maturity Level</h2>
                    <p className="text-gray-600">What it takes to reach the next level of data maturity</p>
                  </div>
                </div>

                {/* Current to Next Level Visualization */}
                <div className="bg-white rounded-xl p-6 mb-8 shadow-sm">
                  <div className="flex items-center justify-between mb-6">
                    {/* Current Level */}
                    <div className="text-center flex-1">
                      <div className="w-20 h-20 mx-auto bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mb-3 shadow-lg">
                        <span className="text-3xl font-bold text-white">
                          {fullResults.ai_analysis.level_progression.current_level?.level_number || Math.floor(fullResults.overall_score)}
                        </span>
                      </div>
                      <div className="font-bold text-gray-900">Current Level</div>
                      <div className="text-sm text-gray-500">
                        {fullResults.ai_analysis.level_progression.current_level?.level_name || fullResults.overall_maturity_level}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">Score: {fullResults.overall_score}/5.0</div>
                    </div>

                    {/* Arrow with Time */}
                    <div className="flex-1 px-4">
                      <div className="relative">
                        <div className="h-2 bg-gradient-to-r from-amber-400 via-emerald-400 to-emerald-500 rounded-full" />
                        <div className="absolute -top-1 left-0 w-4 h-4 bg-amber-500 rounded-full border-2 border-white shadow" />
                        <div className="absolute -top-1 right-0 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white shadow" />
                        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 text-center">
                          <div className="flex items-center gap-1 bg-emerald-100 px-3 py-1 rounded-full">
                            <Clock className="w-3 h-3 text-emerald-600" />
                            <span className="text-xs font-medium text-emerald-700">
                              {fullResults.ai_analysis.level_progression.progression_requirements?.time_estimate || '6-9 months'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Next Level */}
                    <div className="text-center flex-1">
                      <div className="w-20 h-20 mx-auto bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center mb-3 shadow-lg">
                        <span className="text-3xl font-bold text-white">
                          {fullResults.ai_analysis.level_progression.next_level?.level_number || Math.min(Math.floor(fullResults.overall_score) + 1, 5)}
                        </span>
                      </div>
                      <div className="font-bold text-gray-900">Target Level</div>
                      <div className="text-sm text-emerald-600 font-medium">
                        {fullResults.ai_analysis.level_progression.next_level?.level_name || 'Next Level'}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">Target: {fullResults.ai_analysis.level_progression.next_level?.target_score || '3.5-4.0'}</div>
                    </div>
                  </div>

                  {/* Effort Level Badge */}
                  <div className="text-center">
                    <span className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-medium">
                      <Zap className="w-4 h-4" />
                      Effort: {fullResults.ai_analysis.level_progression.progression_requirements?.effort_level || 'Moderate to High'}
                    </span>
                  </div>
                </div>

                {/* Resource Requirements Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  {/* Time */}
                  <div className="bg-white rounded-xl p-5 shadow-sm border border-emerald-100">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Clock className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="font-bold text-gray-900">Time</div>
                    </div>
                    <div className="text-2xl font-bold text-blue-600 mb-1">
                      {fullResults.ai_analysis.level_progression.progression_requirements?.time_estimate || '6-9 months'}
                    </div>
                    <div className="text-xs text-gray-500">Estimated duration</div>
                  </div>

                  {/* Manpower */}
                  <div className="bg-white rounded-xl p-5 shadow-sm border border-emerald-100">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Users className="w-5 h-5 text-purple-600" />
                      </div>
                      <div className="font-bold text-gray-900">Manpower</div>
                    </div>
                    <div className="text-lg font-bold text-purple-600 mb-1">
                      {fullResults.ai_analysis.level_progression.progression_requirements?.manpower_resources?.internal_fte || 'Not available'}
                    </div>
                    <div className="text-xs text-gray-500">
                      + {fullResults.ai_analysis.level_progression.progression_requirements?.manpower_resources?.external_support || '2-3 consultants'}
                    </div>
                  </div>

                  {/* Investment */}
                  <div className="bg-white rounded-xl p-5 shadow-sm border border-emerald-100">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div className="font-bold text-gray-900">Investment</div>
                    </div>
                    <div className="text-lg font-bold text-emerald-600 mb-1">
                      {fullResults.ai_analysis.level_progression.progression_requirements?.investment_required?.total_range || 'Not available'}
                    </div>
                    <div className="text-xs text-gray-500">Total estimated cost</div>
                  </div>

                  {/* ROI / Benefits */}
                  <div className="bg-white rounded-xl p-5 shadow-sm border border-emerald-100">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-amber-600" />
                      </div>
                      <div className="font-bold text-gray-900">Expected ROI</div>
                    </div>
                    <div className="text-lg font-bold text-amber-600 mb-1">
                      {fullResults.ai_analysis.level_progression.progression_requirements?.business_benefits?.quantified_value || 'Not available'}
                    </div>
                    <div className="text-xs text-gray-500">Annual value created</div>
                  </div>
                </div>

                {/* Investment Breakdown */}
                <div className="bg-white rounded-xl p-6 mb-8 shadow-sm">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Coins className="w-5 h-5 text-emerald-600" />
                    Investment Breakdown
                  </h3>
                  <div className="grid md:grid-cols-4 gap-4">
                    {[
                      { label: 'Technology', value: fullResults.ai_analysis.level_progression.progression_requirements?.investment_required?.technology, icon: Database, color: 'indigo' },
                      { label: 'Personnel', value: fullResults.ai_analysis.level_progression.progression_requirements?.investment_required?.personnel, icon: Users, color: 'purple' },
                      { label: 'Training', value: fullResults.ai_analysis.level_progression.progression_requirements?.investment_required?.training, icon: Award, color: 'blue' },
                      { label: 'Consulting', value: fullResults.ai_analysis.level_progression.progression_requirements?.investment_required?.consulting, icon: Briefcase, color: 'teal' }
                    ].map((item, idx) => (
                      <div key={idx} className={`bg-${item.color}-50 rounded-lg p-4 border border-${item.color}-100`}>
                        <div className="flex items-center gap-2 mb-2">
                          <item.icon className={`w-4 h-4 text-${item.color}-600`} />
                          <span className="text-sm font-medium text-gray-700">{item.label}</span>
                        </div>
                        <div className={`text-lg font-bold text-${item.color}-600`}>
                          {item.value || 'TBD'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Business Benefits */}
                <div className="bg-white rounded-xl p-6 mb-8 shadow-sm">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                    Business Benefits at Next Level
                  </h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                      { label: 'Efficiency Gains', value: fullResults.ai_analysis.level_progression.progression_requirements?.business_benefits?.efficiency_gains, icon: Zap, color: 'emerald' },
                      { label: 'Revenue Impact', value: fullResults.ai_analysis.level_progression.progression_requirements?.business_benefits?.revenue_impact, icon: TrendingUp, color: 'blue' },
                      { label: 'Risk Reduction', value: fullResults.ai_analysis.level_progression.progression_requirements?.business_benefits?.risk_reduction, icon: Shield, color: 'amber' },
                      { label: 'Time to Insight', value: fullResults.ai_analysis.level_progression.progression_requirements?.business_benefits?.time_to_insight, icon: Clock, color: 'purple' }
                    ].map((item, idx) => (
                      <div key={idx} className="bg-gradient-to-br from-gray-50 to-white rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center gap-2 mb-2">
                          <item.icon className={`w-4 h-4 text-${item.color}-600`} />
                          <span className="text-sm font-medium text-gray-600">{item.label}</span>
                        </div>
                        <div className="text-lg font-bold text-gray-900">
                          {item.value || 'Significant improvement'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Milestones Roadmap */}
                {fullResults.ai_analysis.level_progression.milestones?.length > 0 && (
                  <div className="bg-white rounded-xl p-6 mb-8 shadow-sm">
                    <h3 className="font-bold text-gray-900 mb-6 flex items-center gap-2">
                      <Flag className="w-5 h-5 text-emerald-600" />
                      Key Milestones to Next Level
                    </h3>
                    <div className="relative">
                      {/* Timeline Line */}
                      <div className="absolute left-6 top-8 bottom-8 w-0.5 bg-gradient-to-b from-emerald-400 to-teal-500" />
                      
                      <div className="space-y-6">
                        {fullResults.ai_analysis.level_progression.milestones.map((milestone, idx) => (
                          <div key={idx} className="relative flex gap-4">
                            {/* Milestone Node */}
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${
                              idx === fullResults.ai_analysis.level_progression.milestones.length - 1
                                ? 'bg-gradient-to-br from-emerald-500 to-teal-600'
                                : 'bg-gradient-to-br from-emerald-400 to-emerald-500'
                            } shadow-lg`}>
                              <span className="text-white font-bold">{milestone.milestone_number || idx + 1}</span>
                            </div>
                            
                            {/* Milestone Content */}
                            <div className="flex-1 bg-gradient-to-r from-emerald-50 to-transparent rounded-lg p-4 border border-emerald-100">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-bold text-gray-900">{milestone.name}</h4>
                                <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {milestone.target_month}
                                </span>
                              </div>
                              <div className="text-sm text-gray-600 mb-2">
                                <strong>Deliverables:</strong>
                                <ul className="list-disc list-inside ml-2 mt-1">
                                  {(milestone.key_deliverables || []).slice(0, 3).map((d, i) => (
                                    <li key={i} className="text-gray-600">{d}</li>
                                  ))}
                                </ul>
                              </div>
                              <div className="text-xs text-emerald-600 font-medium">
                                ✓ Success: {milestone.success_criteria}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Key Challenges & Quick Wins */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Challenges */}
                  {fullResults.ai_analysis.level_progression.key_challenges?.length > 0 && (
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-amber-100">
                      <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                        Key Challenges to Overcome
                      </h3>
                      <ul className="space-y-2">
                        {fullResults.ai_analysis.level_progression.key_challenges.map((challenge, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                            <div className="w-5 h-5 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                              <span className="text-amber-700 text-xs font-bold">{idx + 1}</span>
                            </div>
                            {challenge}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Quick Wins */}
                  {fullResults.ai_analysis.level_progression.quick_wins_to_accelerate?.length > 0 && (
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-emerald-100">
                      <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <Rocket className="w-5 h-5 text-emerald-600" />
                        Quick Wins to Accelerate
                      </h3>
                      <ul className="space-y-2">
                        {fullResults.ai_analysis.level_progression.quick_wins_to_accelerate.map((win, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                            <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                            {win}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Key Roles Needed */}
                {fullResults.ai_analysis.level_progression.progression_requirements?.manpower_resources?.key_roles_needed?.length > 0 && (
                  <div className="bg-white rounded-xl p-6 mt-6 shadow-sm">
                    <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <UserPlus className="w-5 h-5 text-purple-600" />
                      Key Roles Needed
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {fullResults.ai_analysis.level_progression.progression_requirements.manpower_resources.key_roles_needed.map((role, idx) => (
                        <span key={idx} className="px-4 py-2 bg-purple-50 text-purple-700 rounded-full text-sm font-medium border border-purple-200">
                          {role}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* AGI Insights */}
            <div className="mt-8" data-testid="agi-insights-section">
              <AGIInsightsTab
                assessmentType="Data Maturity"
                assessmentId={assessmentId || results?.assessment_id || results?.id}
              />
            </div>

            <ReportSecurityNote />

            {/* Actions */}
            <div className="flex justify-center gap-4">
              <Link to="/">
                <Button variant="outline" className="gap-2">
                  <Home className="w-4 h-4" />
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      );
    }

    // Sample Results (before code verification)
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-white py-8">
        <div className="max-w-4xl mx-auto px-4">
          {/* Sample Header */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <div className="flex justify-between items-start mb-6">
              <div>
                <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm font-medium mb-4">
                  <AlertCircle className="w-4 h-4" />
                  Sample Preview
                </div>
                <h1 className="text-3xl font-bold text-gray-900">{sample.company_name}</h1>
                <p className="text-gray-500">{sample.industry} • Data Maturity Assessment</p>
              </div>
              <div className="text-right">
                <div className={`text-5xl font-bold ${getScoreColor(sample.overall_score)}`}>
                  {sample.overall_score}/5.0
                </div>
                <div className="text-lg font-medium" style={{ color: sample.maturity_color }}>
                  {sample.overall_maturity_level}
                </div>
              </div>
            </div>

            {/* Executive Summary Preview */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-bold text-gray-900 mb-3">Executive Summary (Preview)</h3>
              <p className="text-gray-600">{sample.executive_summary_preview}</p>
              <div className="mt-3 p-2 bg-gray-100 rounded border border-dashed border-gray-300">
                <p className="text-sm text-gray-500 italic">Full executive summary available with access code</p>
              </div>
            </div>
          </div>

          {/* Sample Dimension Scores */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Dimension Scores (Sample)</h2>
            <div className="space-y-4">
              {sample.dimension_scores?.slice(0, 3).map((dim) => {
                const isUnderperforming = dim.gap_from_benchmark > 0.3;
                return (
                  <div key={dim.dimension_id} className={`p-4 rounded-xl border-2 ${isUnderperforming ? 'border-red-200 bg-red-50' : 'border-gray-100 bg-gray-50'}`}>
                    <div className="flex justify-between items-center">
                      <span className={`font-semibold ${isUnderperforming ? 'text-red-800' : 'text-gray-900'}`}>
                        {dim.dimension_name}
                      </span>
                      <div className="flex items-center gap-3">
                        <span className={`text-xl font-bold ${getScoreColor(dim.score)}`}>
                          {dim.score}/5.0
                        </span>
                        {isUnderperforming && (
                          <span className="px-2 py-1 rounded-full text-xs font-bold bg-red-600 text-white">
                            BELOW BENCHMARK
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 p-3 bg-gray-50 rounded border border-dashed border-gray-300">
              <p className="text-sm text-gray-500 italic">
                {sample.dimension_scores?.length - 3} more dimensions available with full access
              </p>
            </div>
          </div>

          {/* Sample Pain Points */}
          {sample.critical_pain_points?.length > 0 && (
            <div className="bg-white rounded-2xl shadow-lg border-2 border-red-200 p-8 mb-8">
              <h2 className="text-xl font-bold text-red-700 mb-6 flex items-center gap-2">
                <AlertTriangle className="w-6 h-6" />
                ⚠️ Critical Pain Points Identified
              </h2>
              <div className="space-y-4">
                {sample.critical_pain_points.map((pp, idx) => (
                  <div key={idx} className="p-4 bg-red-50 rounded-xl border border-red-200">
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-bold text-red-800">{pp.area}</span>
                      <span className="px-2 py-1 rounded-full text-xs font-bold bg-red-600 text-white">
                        {pp.severity}
                      </span>
                    </div>
                    <p className="text-red-700 text-sm">{pp.current_impact}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-300">
                <p className="text-sm text-amber-800 font-medium">
                  📋 <strong>Detailed recommendations and action plans are available in the full report.</strong>
                </p>
              </div>
            </div>
          )}

          {/* Access Code Section */}
          <div className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl p-8 text-white text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Unlock Full Assessment Report</h2>
            <p className="text-indigo-100 mb-6 max-w-md mx-auto">
              Enter your 6-digit secret code to access the complete report with detailed analysis, 
              roadmap, technology recommendations, and downloadable presentation.
            </p>
            
            <div className="max-w-sm mx-auto">
              <div className="flex gap-2 mb-3">
                <Input
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={secretCode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setSecretCode(val);
                    setCodeError('');
                  }}
                  className="text-center text-lg tracking-widest font-mono bg-white text-gray-900"
                  maxLength={6}
                  data-testid="secret-code-input"
                />
                <Button 
                  onClick={verifySecretCode}
                  disabled={verifyingCode || secretCode.length !== 6}
                  className="bg-white text-indigo-600 hover:bg-indigo-50 px-6"
                  data-testid="verify-code-btn"
                >
                  {verifyingCode ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Unlock'}
                </Button>
              </div>
              
              {codeError && (
                <p className="text-red-200 text-sm mb-3">{codeError}</p>
              )}
              
              <p className="text-indigo-200 text-sm">
                For full report, please enter secret code.
              </p>
              <p className="text-indigo-100 text-sm mt-2">
                Contact <strong>Vijay Karna</strong> at{' '}
                <a href="mailto:karna.vijay@gmail.com" className="underline hover:text-white">
                  karna.vijay@gmail.com
                </a>
              </p>
            </div>
          </div>

          {/* Back to Home */}
          <div className="text-center mt-8">
            <Link to="/">
              <Button variant="outline" className="gap-2">
                <Home className="w-4 h-4" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-white flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
        <p className="text-gray-600">Loading assessment...</p>
      </div>
    </div>
  );
};

export default DataMaturityPage;
